import { IReactionDisposer, reaction } from 'mobx';
import { types, Instance } from 'mobx-state-tree';
import InvokerType from './InvokerType';
import { RTModel } from './RTModel';
import config from 'config';
import type { CallDiagnosticContext, CallDiagnosticRecord } from 'call/diagnostics';
import type { JaasDevice } from 'call/jitsi/deviceIdentity';

export type CallProvider = 'dailyco' | 'jitsi';

/**
 * What `refresh_call` hands back: the room to join and the provider that owns
 * it. The backend decides the provider per room (the `TutorCallProvider` row,
 * set in sq_api's admin), so every participant of a room lands on the same one.
 */
export interface ICallConfig {
  provider: CallProvider;
  url: string;
  token: string;
  /** Jitsi only: the JaaS deployment host, tenant AppID and bare room name. */
  domain?: string;
  appId?: string;
  roomName?: string;
  /**
   * Jitsi only: the JaaS device this user is already billed as, as a map of
   * the local-storage keys that make it up. `{}` (or absent) means the backend
   * has none yet and the client should report the one lib-jitsi-meet mints.
   * An object rather than a single id so a change to what 8x8 counts as a
   * device is data, not a schema change. See src/call/jitsi/deviceIdentity.ts.
   */
  device?: JaasDevice;
}

export const MediaStore = types
  .model('MediaStore', {
    audioMuted: false,
    videoMuted: false,
    audioOutMuted: false,
    noiseCancellationEnabled: false,
    micLocked: false
  })
  .actions((self) => ({
    toggleVideo() {
      self.videoMuted = !self.videoMuted;
    },
    toggleAudio() {
      self.audioMuted = !self.audioMuted;
    },
    setAudioMuted(state: boolean) {
      self.audioMuted = state;
    },
    setVideoMuted(state: boolean) {
      self.videoMuted = state;
    },
    setMicLocked(state: boolean) {
      self.micLocked = state;
    },
    toggleAudioOut() {
      self.audioOutMuted = !self.audioOutMuted;
    },
    toggleNoiseCancellation() {
      self.noiseCancellationEnabled = !self.noiseCancellationEnabled;
    },
  }))
  .actions((self) => {
    let disposerAudio: IReactionDisposer | null = null;
    let disposerVideo: IReactionDisposer | null = null;

    function localize() {
      // Only restore and persist video state when student camera controls are enabled.
      // When disabled, cameras always start on (videoMuted stays false).
      if (config.SHOW_STUDENT_CAMERA_CONTROLS) {
        const storedVideo = window.localStorage.getItem('MediaStore.videoMuted');
        if (storedVideo) self.videoMuted = storedVideo === '1';

        disposerVideo = reaction(
          () => self.videoMuted,
          (muted) => {
            window.localStorage.setItem('MediaStore.videoMuted', muted ? '1' : '0');
          }
        );
      }

      const storedAudio = window.localStorage.getItem('MediaStore.audioMuted');
      if (storedAudio) self.audioMuted = storedAudio === '1';

      disposerAudio = reaction(
        () => self.audioMuted,
        (muted) => {
          window.localStorage.setItem('MediaStore.audioMuted', muted ? '1' : '0');
        }
      );
    }

    function beforeDestroy() {
      disposerVideo && disposerVideo();
      disposerAudio && disposerAudio();
    }
    return { localize, beforeDestroy };
  });

export interface IMediaStore extends Instance<typeof MediaStore> { }

export const CallStore = RTModel.named('CallStore')
  .props({
    mediaStoreMap: types.map(MediaStore),
    roomName: types.maybe(types.string),
    /**
     * Whether this call is a Masterclass, which stays on Daily and does not
     * follow the administrator's provider switch. The backend always sends it.
     *
     * Defaulted rather than required because of what a required field costs
     * when one is missing: RT's `subscribe_success` applies the snapshot inside
     * errorWrapped, which swallows the validation throw but still marks the
     * subscription subscribed, so the node is never created and every call —
     * Masterclass included — sits on the loading screen with no error surfaced
     * and nothing a reload can fix. Defaulting confines that blast radius to
     * the rare call type, and the backend is authoritative about the provider
     * anyway: `call_provider` returns 'dailyco' for a Masterclass whatever this
     * client believes, so a Masterclass misread as non-Masterclass follows the
     * global revision but is still handed Daily, and only rejoins its own room.
     */
    isMasterclass: false,
    refresh_call: InvokerType,
    store_call_device: InvokerType,
    report_call_diagnostics: InvokerType,
  })
  .actions((self) => ({
    /**
     * `stale_room_name` names the Daily room a recovering client was thrown
     * out of. The server replaces only that room, so a room that dies for the
     * whole class is replaced once rather than once per client (which used to
     * leave everyone in a private room of their own). Jitsi ignores it: a JaaS
     * room is never replaced, its token is re-minted.
     */
    refreshCall(create_new = false, stale_room_name?: string, diagnostic_context?: CallDiagnosticContext) {
      return self.refresh_call.invoke({ create_new, stale_room_name, diagnostic_context }) as Promise<ICallConfig>;
    },
    /** Best-effort telemetry bypasses the socket's reliable replay buffer in RTControl. */
    reportCallDiagnostics(events: CallDiagnosticRecord[]) {
      self.report_call_diagnostics.invokeAndForget({ events });
    },
    /**
     * Report the JaaS device lib-jitsi-meet minted for this user, so their
     * other browsers are handed the same one and 8x8 counts them once.
     * Fire-and-forget: the join has already succeeded by the time this runs,
     * and a lost report costs one monthly active user, not a broken call.
     * The server records it first-write-wins.
     */
    storeCallDevice(device: JaasDevice) {
      self.store_call_device.invokeAndForget({ device });
    },
    setMuteStateForAll(mute: boolean) {
      self.mediaStoreMap.forEach((remoteControl) => {
        remoteControl.setAudioMuted(mute);
      });
    },
    setMicLockStateForAll(lock: boolean, exceptUsernames?: Set<string>) {
      self.mediaStoreMap.forEach((remoteControl, username) => {
        if (exceptUsernames && exceptUsernames.has(username.toString())) return;
        remoteControl.setMicLocked(lock);
        if (lock) {
          remoteControl.setAudioMuted(true);
        }
      });
    }
  }));

export interface ICallStore extends Instance<typeof CallStore> { }
